// Gallery — backend-routed client.
//
// Phase 2.6 of the GCP migration: replaces the previous frontend-direct
// supabase-js path (supabase.storage.upload + supabase.from('posts').*).
// All gallery I/O now flows through backend endpoints (POST/GET/DELETE
// /api/gallery/posts) which talk to Cloud SQL + GCS internally.
//
// The exported API surface is unchanged so the 11 call sites keep working
// without per-file edits. Two notable simplifications:
//   1. Posts now carry a direct `image_url` (GCS public URL — gallery
//      bucket is public, no signing needed).
//   2. getSignedImageUrl/getSignedImageUrls are kept as named exports but
//      return the GCS public URL directly (no backend round-trip). The
//      `transform` option is ignored — Supabase's Image Transformations
//      was the original cost driver, now irrelevant on GCS.

import { API_BASE, getAuthHeaders, mapFailure, NetworkError } from './api';

// Bucket name override — defaults to the prod gallery bucket. Set
// VITE_GCS_GALLERY_BUCKET in your env if you ever point at a different
// project's bucket from a local build.
const GALLERY_BUCKET =
  (import.meta.env.VITE_GCS_GALLERY_BUCKET as string | undefined) ||
  'socialposts-ai-prod-gallery';

/** One entry in posts.published_to. Appended each time the post is
 *  successfully published to a channel via Post Now (or, eventually,
 *  the scheduled-publish worker). v1 only writes channel='facebook'. */
export interface PostPublication {
  channel: 'facebook' | 'instagram' | 'tiktok' | string;
  posted_at: string;          // ISO 8601 UTC
  external_post_id: string;   // platform's post id
  page_id?: string;
  page_name?: string;
}

export interface Post {
  id: string;
  user_id: string;
  /** brand_id is non-null for new rows; older legacy rows may be null. */
  brand_id?: string | null;
  image_path: string;
  /** Direct public URL for <img src>. Server-rendered from gcs_public_url. */
  image_url?: string;
  /** Small baked WEBP thumbnail for small-tile surfaces (the calendar). Null on
   *  posts that predate the feature — callers fall back to image_url. */
  thumbnail_url?: string | null;
  template_id: string | null;
  aspect_ratio: string;
  headline: string | null;
  tagline: string | null;
  title: string | null;
  created_at: string;
  /** Optional FK back to the Ad Strategist analysis this post came from. */
  source_job_id: string | null;
  /** Optional FK back to the brand-setup batch this post came from. Set
   *  on tiles generated by the onboarding wizard's 20-render batch.
   *  Either source_job_id OR source_setup_job_id (or neither) — not both. */
  source_setup_job_id: string | null;
  /** Brand-setup batches insert the first N as unlocked=true, rest false.
   *  Gallery renders locked tiles with a blur overlay + Unlock CTA. All
   *  non-batch rows (wizard saves etc.) default true. */
  unlocked: boolean;
  /** "Starred" shortlist flag — user marks keeper ads (tap the ★ on a tile).
   *  Drives the All / Starred filter in My Creatives. Defaults false. */
  starred: boolean;
  /** Append-only log of successful publications. Empty array for posts
   *  that haven't been published anywhere. Drives the
   *  "Posted Jun 6 · Facebook" pill on the tile. */
  published_to: PostPublication[];
  /** Per-render API cost (USD). Drives the red diagnostic cost pill in
   *  the top-left of each tile (added June 7 to reconcile the documented
   *  Gemini Flash 3.1 price ($0.067) against observed prod spend (~$0.50).
   *  Null for historical posts pre-tracking + Save-to-Gallery uploads
   *  (those use pre-rendered wizard bytes — cost not yet plumbed). */
  generation_cost_usd: number | null;
  /** Image-gen provider that rendered the post ('openai' / 'gemini' /
   *  'ideogram'). Shown next to the cost in the pill, e.g. "gemini · $0.067".
   *  Null on historical pre-tracking posts — pill just shows cost alone. */
  generation_provider: string | null;
  /** Saved per-channel caption drafts. Keys: 'facebook', 'instagram',
   *  and future channel ids. Empty object when no draft exists for any
   *  channel. The Post Now modal restores this on open instead of firing
   *  a fresh AI suggestion every time; user can hit "Regenerate with AI"
   *  to overwrite. */
  captions: Record<string, string>;
  /** Slot index inside a brand-setup batch (0..plan_len-1). Populated for
   *  stubs AND for rendered/unlocked rows from a batch (slim render_config
   *  marker survives the render UPDATE). Null for all other posts. Frontend
   *  uses this to assign stable sticky positions by slot identity rather
   *  than by non-deterministic parallel-insert arrival order. */
  slot_idx: number | null;
  /** Jul 14 — true when the backend terminally FAILED this render
   *  (render_config.render_error). Lets a stub tile show "Try again"
   *  immediately instead of waiting out the 5-min client stuck-timeout. */
  render_failed?: boolean;
  /** Aug 2 — ISO timestamp stamped when a stub is unlocked (single or bulk).
   *  The stuck-render timer keys on THIS instead of created_at, so unlocking an
   *  old stub keeps its created_at → the tile renders in place in its date
   *  section instead of teleporting to a new "Today" block. */
  queued_at?: string | null;
  /** FK to the prior chain head. Set on every new version created by
   *  /api/creatives/:id/save-version (Jun 17 versioning). Null on root
   *  posts (the first render of a creative). Frontend gallery uses this
   *  to inherit the parent's sticky grid position on save — so editing
   *  a tile and saving keeps it in the same column. */
  parent_post_id: string | null;
  /** True when this row is the current head of its chain — what gallery
   *  shows. /api/gallery/posts filters is_head=true so non-head rows are
   *  never returned, but the field is exposed for future use (e.g.
   *  version-picker rendering on /creatives/:id). */
  is_head: boolean;
  /** Option B re-editable overlays (Jun 17). GCS path + public URL for
   *  the RAW base render PNG (no overlays baked in). The editor reads
   *  base_image_url on reopen so logo + text layers stay editable. Null
   *  on legacy pre-Option-B rows — editor falls back to image_url then,
   *  with empty layers (anything that was baked stays baked, but new
   *  saves move the row into the new model going forward). */
  base_image_path: string | null;
  base_image_url: string | null;
  /** Text/CTA overlay layers persisted at save time. Rehydrated into
   *  `editorOverlays` on the detail page so each layer is re-positionable,
   *  re-colorable, re-typable. Null = no editable layers (legacy or
   *  saved without text). */
  overlay_json: unknown[] | null;
  /** Logo overlay layers — same shape, separate field. */
  logo_overlay_json: unknown[] | null;
  /** Recipe-synth diagnostics breadcrumb (Jun 21), lifted from
   *  render_config.synth. Present only on synth renders (top-up /
   *  Generate-20-more): synth_vibe, donors{scene,layout,type,palette →
   *  "<vibe_slug>/<id>"}, scene fields, and recipe/render palettes. Drives
   *  the dev-only diagnostics panel on /creatives/:id. Null otherwise. */
  synth: Record<string, unknown> | null;
  /** Marquee edit history (Jun 24) — every successful /marquee-edit
   *  pushes the PRIOR base path here so undo can pop it. UI uses the
   *  length to enable/disable the Undo button. */
  base_image_path_history: MarqueeHistoryEntry[];
  /** Text-overlay editor doc (Jun 28), lifted from render_config.text_overlay
   *  on tiles baked from a JSON text template. Present → /creatives renders the
   *  SAME JsonSocialPost (template + slots over base_image_url) for pixel-perfect
   *  editing. Null on every other tile (canvas editor path). */
  text_overlay: TextOverlayDoc | null;
}

/** The editor doc persisted for a text-overlay tile — the EXACT colored
 *  JSON template + slots that were baked, so the editor re-renders identically. */
export interface TextOverlayDoc {
  template_id?: string;
  zone?: string | null;
  text_color?: string;
  template: Record<string, unknown>;       // JsonTemplate (color already applied)
  slots: Record<string, string>;           // SocialPostSlots subset
}

/** Single entry in posts.base_image_path_history. */
export interface MarqueeHistoryEntry {
  path: string;
  edited_at: string;
  rect: { x: number; y: number; w: number; h: number };
  instruction: string;
}

interface RawGalleryRow {
  id: string;
  user_id: string;
  brand_id: string | null;
  image_path: string;
  image_url: string;
  thumbnail_url?: string | null;
  template_id: string | null;
  aspect_ratio: string;
  headline: string | null;
  tagline: string | null;
  title: string | null;
  created_at: string;
  source_job_id: string | null;
  source_setup_job_id?: string | null;
  approved?: boolean;
  starred?: boolean;
  unlocked?: boolean;
  published_to?: PostPublication[];
  generation_cost_usd?: number | null;
  generation_provider?: string | null;
  captions?: Record<string, string>;
  slot_idx?: number | null;
  render_failed?: boolean;
  queued_at?: string | null;
  parent_post_id?: string | null;
  is_head?: boolean;
  base_image_path?: string | null;
  base_image_url?: string | null;
  overlay_json?: unknown[] | null;
  logo_overlay_json?: unknown[] | null;
  synth?: Record<string, unknown> | null;
  base_image_path_history?: MarqueeHistoryEntry[];
  text_overlay?: TextOverlayDoc | null;
}

function rowToPost(r: RawGalleryRow): Post {
  return {
    id: r.id,
    user_id: r.user_id,
    brand_id: r.brand_id,
    image_path: r.image_path,
    image_url: r.image_url,
    thumbnail_url: r.thumbnail_url ?? null,
    template_id: r.template_id,
    aspect_ratio: r.aspect_ratio,
    headline: r.headline,
    tagline: r.tagline,
    title: r.title,
    created_at: r.created_at,
    source_job_id: r.source_job_id,
    source_setup_job_id: r.source_setup_job_id ?? null,
    // Default true for older rows that pre-date the unlocked column.
    unlocked: r.unlocked ?? true,
    starred: r.starred === true,
    published_to: Array.isArray(r.published_to) ? r.published_to : [],
    generation_cost_usd: r.generation_cost_usd ?? null,
    generation_provider: r.generation_provider ?? null,
    slot_idx: typeof r.slot_idx === 'number' ? r.slot_idx : null,
    render_failed: r.render_failed === true,
    queued_at: typeof r.queued_at === 'string' ? r.queued_at : null,
    parent_post_id: r.parent_post_id ?? null,
    is_head: r.is_head ?? true,
    base_image_path: r.base_image_path ?? null,
    base_image_url: r.base_image_url ?? null,
    overlay_json: Array.isArray(r.overlay_json) ? r.overlay_json : null,
    logo_overlay_json: Array.isArray(r.logo_overlay_json) ? r.logo_overlay_json : null,
    synth:
      r.synth && typeof r.synth === 'object' && !Array.isArray(r.synth)
        ? (r.synth as Record<string, unknown>)
        : null,
    base_image_path_history: Array.isArray(r.base_image_path_history)
      ? r.base_image_path_history.filter(
          (h): h is MarqueeHistoryEntry =>
            h !== null &&
            typeof h === 'object' &&
            typeof (h as Record<string, unknown>).path === 'string',
        )
      : [],
    text_overlay:
      r.text_overlay &&
      typeof r.text_overlay === 'object' &&
      r.text_overlay.template &&
      r.text_overlay.slots
        ? r.text_overlay
        : null,
    // Coerce to a plain Record<string, string>. Defensive against legacy
    // rows (column was nullable pre-migration) and bad values from
    // double-encoded jsonb writes. Backend already cleans these but
    // mirroring the defense here means stale tabs without a backend
    // restart don't crash the gallery.
    captions:
      r.captions && typeof r.captions === 'object' && !Array.isArray(r.captions)
        ? (Object.fromEntries(
            Object.entries(r.captions).filter(
              ([k, v]) => typeof k === 'string' && typeof v === 'string',
            ),
          ) as Record<string, string>)
        : {},
  };
}

export interface SavePostInput {
  blob: Blob;
  templateId?: string | null;
  aspectRatio?: string;
  headline?: string | null;
  tagline?: string | null;
  title?: string | null;
  /** Default 'image/png'. Override to 'application/zip' for carousel zips. */
  contentType?: string;
  /** Default 'png'. Override to 'zip' for carousel zips. */
  fileExt?: string;
  /** Ad Strategist provenance (null for wizard saves). */
  sourceJobId?: string | null;
  /** My Products row id — set when the user picked a library product
   *  (vs a fresh upload). Powers the FB caption suggester which JOINs
   *  to user_products for real product metadata. NULL is fine; the
   *  caption suggester falls back to vision on the rendered image. */
  productId?: string | null;
  /** Image-gen provider that rendered the asset ('openai' / 'gemini' /
   *  'ideogram'). Wizard callers pass whatever was returned in the
   *  /api/generate response so the cost pill shows the same provider
   *  on the saved tile that the user saw during preview. */
  generationProvider?: string | null;
  /** Per-render API cost (USD). Wizard callers pass the `cost` field from
   *  the /api/generate response so the cost pill shows on every saved tile,
   *  not just backend-worker renders. */
  generationCostUsd?: number | null;
  /** Text-overlay tile (Jun 29). `baseImageB64` = the text-free scene
   *  (b64 PNG from the ai-generate response); `textOverlay` = the JSON
   *  editor doc. Pass BOTH so the saved tile persists base_image_path +
   *  render_config.text_overlay and opens in the /creatives overlay editor.
   *  `blob` stays the baked composite (→ image_path / thumbnail). */
  baseImageB64?: string | null;
  textOverlay?: Record<string, unknown> | null;
}

/** Decode a base64 PNG payload into a Blob for multipart upload. */
function b64ToBlob(b64: string, type = 'image/png'): Blob {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type });
}

/**
 * Saves a flattened post to the gallery. Returns the new post's id.
 *
 * Wire: multipart POST → backend uploads to GCS + inserts the row.
 * Soft-transactional on the backend side: failed insert deletes the
 * just-uploaded GCS object to avoid orphans.
 */
export async function savePost(input: SavePostInput): Promise<string> {
  const form = new FormData();
  // Multipart file: `image` is the blob, browser fills filename/content-type
  // from the blob OR we set them explicitly via the form field name.
  form.append('image', input.blob);
  if (input.templateId) form.append('template_id', input.templateId);
  if (input.aspectRatio) form.append('aspect_ratio', input.aspectRatio);
  if (input.headline) form.append('headline', input.headline);
  if (input.tagline) form.append('tagline', input.tagline);
  if (input.title) form.append('title', input.title);
  if (input.sourceJobId) form.append('source_job_id', input.sourceJobId);
  if (input.productId) form.append('product_id', input.productId);
  if (input.contentType) form.append('content_type', input.contentType);
  if (input.fileExt) form.append('file_ext', input.fileExt);
  if (input.generationProvider) form.append('generation_provider', input.generationProvider);
  if (input.generationCostUsd != null) form.append('generation_cost_usd', String(input.generationCostUsd));
  // Text-overlay tile: send the editor doc + the text-free base scene so the
  // backend persists render_config.text_overlay + base_image_path.
  if (input.textOverlay) {
    form.append('text_overlay', JSON.stringify(input.textOverlay));
    if (input.baseImageB64) {
      form.append('base_image', b64ToBlob(input.baseImageB64), 'base.png');
    }
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/gallery/posts`, {
      method: 'POST',
      // NOTE: don't set Content-Type — browser sets multipart boundary
      headers: await getAuthHeaders(),
      body: form,
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return row.id;
}

/**
 * Replace a saved creative's asset with the FINAL composited version
 * (Jun 12 — /appv3 Studio finishing flow). The row id stays stable
 * (scheduled posts + deep links keep working); the backend writes the
 * bytes to a NEW versioned GCS path so public-bucket caching can't serve
 * the stale image. Use after flattening text/logo overlays or a
 * prompt-edit so My Creatives holds ONE finished creative, not a trail
 * of duplicates.
 */
export async function replacePostImage(input: {
  postId: string;
  blob: Blob;
  contentType?: string;
  fileExt?: string;
}): Promise<string> {
  const form = new FormData();
  form.append('image', input.blob);
  if (input.contentType) form.append('content_type', input.contentType);
  if (input.fileExt) form.append('file_ext', input.fileExt);

  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/api/gallery/posts/${encodeURIComponent(input.postId)}/replace-image`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: form,
      },
    );
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return row.id;
}

/**
 * List posts for the active brand (or pass brandId to override). Newest first.
 * Backend filters by user_id + brand_id; cross-user leakage is impossible.
 */
/** Keyset cursor for "Load older creatives" — the last (oldest) loaded
 *  post's created_at + id. Pass to listPosts to fetch the next 200. */
export interface GalleryCursor {
  beforeCreatedAt: string;
  beforeId: string;
}

export async function listPosts(
  brandId?: string | null,
  cursor?: GalleryCursor | null,
): Promise<Post[]> {
  // Cache-busting: a unique `_t` per call guarantees that no
  // intermediary (browser fetch cache, dev-server proxy, anything) can
  // serve a stale response on polling-triggered re-fetches during
  // brand-setup. cache: 'no-store' alone wasn't holding the line —
  // adding a unique query string forces a network round-trip.
  const params = new URLSearchParams();
  if (brandId) params.set('brand_id', brandId);
  if (cursor?.beforeCreatedAt && cursor?.beforeId) {
    params.set('before_created_at', cursor.beforeCreatedAt);
    params.set('before_id', cursor.beforeId);
  }
  params.set('_t', String(Date.now()));
  const qs = `?${params.toString()}`;
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/gallery/posts${qs}`, {
      headers: await getAuthHeaders(),
      cache: 'no-store',
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const rows = (await response.json()) as RawGalleryRow[];
  if (!Array.isArray(rows)) return [];
  return rows.map(rowToPost);
}

/**
 * Transform options (kept for API compat; ignored on GCS where the
 * gallery bucket is public and serves originals directly).
 */
export interface ImageTransform {
  width?: number;
  height?: number;
  quality?: number;
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Build the GCS public URL for an image_path. Synchronous-feel (no
 * round-trip) but kept async to match the previous Supabase API so the
 * 11 call sites don't need code changes.
 *
 * `transform` is accepted but ignored — gallery is on GCS now; resizing
 * happens via pre-baked thumbnails (TODO at save time) rather than an
 * edge transformer. Existing callers like `width: 500` get the original.
 */
export async function getSignedImageUrl(
  imagePath: string,
  // legacy params kept for signature compat — both ignored on GCS
  _expiresInSeconds: number = 3600,
  _transform?: ImageTransform,
): Promise<string> {
  return `https://storage.googleapis.com/${GALLERY_BUCKET}/${imagePath}`;
}

/**
 * Batched companion to getSignedImageUrl. Returns map keyed by image_path.
 * Constant-time + zero network — every value is built locally from the
 * GCS public-URL pattern.
 */
export async function getSignedImageUrls(
  imagePaths: string[],
  _expiresInSeconds: number = 3600,
  _transform?: ImageTransform,
): Promise<Record<string, string>> {
  const map: Record<string, string> = {};
  for (const p of imagePaths) {
    if (!p) continue;
    map[p] = `https://storage.googleapis.com/${GALLERY_BUCKET}/${p}`;
  }
  return map;
}

/**
 * Delete a post — backend removes both the GCS object and the posts row.
 * The legacy signature took (postId, imagePath); imagePath is no longer
 * needed (backend looks it up) but kept for source-compat with existing
 * call sites.
 */
export async function deletePost(
  postId: string,
  _imagePath?: string,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/gallery/posts/${postId}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
}

/**
 * Single-post lookup by id. Powers the /creatives/:id detail page on
 * cold loads (direct URL / refresh) when the post isn't already in the
 * gallery's in-memory list.
 */
export async function getPost(postId: string): Promise<Post> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/gallery/posts/${postId}`, {
      headers: await getAuthHeaders(),
      cache: 'no-store',
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return rowToPost(row);
}

/**
 * Re-bake a text-overlay tile with edited slot copy. Backend re-renders the
 * SAME JsonSocialPost (stored colored template + scene) with the new slots →
 * the updated tile stays pixel-identical to what the editor showed. Free.
 * Returns the updated Post (new image_url + persisted slots).
 */
export async function saveOverlay(
  postId: string,
  slots: Record<string, string>,
  template?: Record<string, unknown> | null,
): Promise<Post> {
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/api/creatives/${encodeURIComponent(postId)}/save-overlay`,
      {
        method: 'POST',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify(template ? { slots, template } : { slots }),
      },
    );
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return rowToPost(row);
}

/**
 * Turn a rendered ad's BAKED-IN text into editable overlays (Jul 12). Backend
 * removes the text (gpt-image) → a clean plate, and vision-reads the text +
 * positions → editable overlays. Costs 2 credits (charged once). Returns the
 * updated Post now carrying base_image_url + text_overlay, so the caller can
 * re-render into the OverlayEditor. Idempotent + free if already prepared.
 */
export async function prepareTextEdit(
  postId: string,
  opts?: { frameIndex?: number; signal?: AbortSignal },
): Promise<Post> {
  // Carousels are per-slide: frame_index (0-based) cleans just that slide (2 cr).
  const qs =
    opts?.frameIndex != null ? `?frame_index=${encodeURIComponent(opts.frameIndex)}` : '';
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/api/creatives/${encodeURIComponent(postId)}/prepare-text-edit${qs}`,
      { method: 'POST', headers: await getAuthHeaders(), signal: opts?.signal },
    );
  } catch (err) {
    // Let an AbortError (user canceled) propagate as-is so the caller can tell it
    // apart from a real network failure.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return rowToPost(row);
}

/**
 * Unlock a locked tile. Two backend paths:
 *  - STUB (no image_path, has render_config): backend reserves 1 credit,
 *    fires the render (~25s Gemini call), uploads, fills image_path, flips
 *    unlocked=true. Returns the fully-rendered post. Throws on insufficient
 *    credits (402) or render failure (500).
 *  - ALREADY-RENDERED (image_path set, unlocked=false): backend just flips
 *    the flag. Returns the updated post. Free + fast.
 * Either way, the response is the updated `Post` which the caller swaps
 * into local state to reveal the tile.
 */
/**
 * Save (or clear) a per-channel caption draft for a post. The Post Now
 * modal restores this on next open instead of firing a fresh AI
 * suggestion every time, so the user's edits don't get blown away.
 *
 * Channel ids match the rest of the app's vocabulary: 'facebook',
 * 'instagram', 'tiktok', 'x'. Send caption='' to clear the slot.
 *
 * Returns the updated `Post` so the caller can swap it into local state
 * (keeps `post.captions[channel]` consistent across the gallery without
 * a full refetch).
 *
 * Fire-and-forget at most callsites — the Post Now modal already shows
 * the saved value locally; persistence is for re-open + cross-device.
 * Catch network errors at the call site (we don't want a save blip to
 * block publishing).
 */
/** Toggle the "Starred" shortlist flag on a post. Returns the updated post. */
export async function setPostStarred(
  postId: string,
  starred: boolean,
): Promise<Post> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/gallery/posts/${postId}/star`, {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ starred }),
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return rowToPost(row);
}

export async function saveCaption(
  postId: string,
  channel: string,
  caption: string,
): Promise<Post> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/gallery/posts/${postId}/caption`, {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ channel, caption }),
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return rowToPost(row);
}

/**
 * Unlock a single posts row. SYNC (~25s) — caller blocks on the HTTP
 * response. Backend processes one render at a time per HTTP request, so
 * the frontend MUST queue concurrent unlock clicks (see gallery/page.tsx
 * processQueue pattern). Successful response = fully-rendered Post with
 * image_path set.
 *
 * Throws InsufficientCreditsError (402), NetworkError, etc.
 */
export async function unlockPost(postId: string): Promise<Post> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/gallery/posts/${postId}/unlock`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return rowToPost(row);
}

/** Bulk-unlock every locked stub in a brand-setup batch. Reserves the credits,
 *  marks the stubs paid+queued (so they render in the background as a Cloud
 *  Task), and returns fast with the queued count. Unlike firing N individual
 *  unlockPost() calls, the render state becomes DB-derived (isRendering) and
 *  survives navigating to the editor and back. */
export async function unlockAllBatch(setupJobId: string): Promise<{ queued: number }> {
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/api/gallery/batches/${setupJobId}/unlock-all`,
      { method: 'POST', headers: await getAuthHeaders() },
    );
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  return response.json();
}

/**
 * Commit a new VERSION of a creative (Jun 17). The flattened PNG body
 * becomes the new chain head; old head demoted to is_head=false. Returns
 * the new Post; caller can navigate to `/creatives/${post.id}` (or stay
 * on the source URL — both load the chain's head).
 */
export async function saveCreativeVersion(input: {
  sourcePostId: string;
  /** The flattened composite — what /gallery shows + Download writes. */
  blob: Blob;
  /** Option B: the raw base render (un-flattened). Omit for legacy save
   *  (server-side falls back to flat-only mode); the new editor always
   *  sends it so the next reopen has editable layers. */
  baseBlob?: Blob;
  /** TextOverlay[] / LogoOverlay[] arrays — sent verbatim as JSON
   *  strings. Server validates as JSON-array before INSERTing into
   *  the jsonb columns. */
  overlayJson?: unknown[];
  logoOverlayJson?: unknown[];
  title?: string | null;
}): Promise<Post> {
  const form = new FormData();
  // Jun 22 — carousel support: derive filename ext from blob.type so the
  // backend's content-type detection (content_type → filename) lands on
  // 'zip' for carousels and 'png' for singles. Backend hardcoded .png
  // paths before; now branches on extension.
  const imageExt = input.blob.type === 'application/zip' ? 'zip' : 'png';
  form.append('image', input.blob, `version.${imageExt}`);
  if (input.baseBlob) {
    const baseExt = input.baseBlob.type === 'application/zip' ? 'zip' : 'png';
    form.append('base_image', input.baseBlob, `version-base.${baseExt}`);
  }
  if (input.overlayJson) form.append('overlay_json', JSON.stringify(input.overlayJson));
  if (input.logoOverlayJson) form.append('logo_overlay_json', JSON.stringify(input.logoOverlayJson));
  if (input.title) form.append('title', input.title);
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/api/creatives/${input.sourcePostId}/save-version`,
      { method: 'POST', headers: await getAuthHeaders(), body: form },
    );
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return rowToPost(row);
}

/**
 * List all versions in a creative's chain (Jun 17). Oldest-first. Powers
 * the version picker on /creatives/:id.
 */
export async function listCreativeVersions(postId: string): Promise<Post[]> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/creatives/${postId}/versions`, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const rows = (await response.json()) as RawGalleryRow[];
  return rows.map(rowToPost);
}

/**
 * Reimagine a post via a user-supplied prompt. Backend reserves 1 credit,
 * passes the source image to the AI provider chain as a transformation
 * reference, inserts a NEW sibling tile inheriting brand/product/aspect.
 * Original is preserved. Returns the new Post.
 *
 * Throws InsufficientCreditsError (402), or generic 4xx/5xx on failure.
 */
/**
 * Marquee edit (Jun 24) — POST a rectangular region + instruction; backend
 * builds a feathered mask + calls gpt-image-2 mask edit. Operates on
 * posts.base_image_path so text/logo overlays stay editable. 1 credit.
 * Returns the updated Post (same id; base_image_path swapped).
 */
export async function marqueeEditPost(
  postId: string,
  rect: { x: number; y: number; w: number; h: number },
  instruction: string,
): Promise<Post> {
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/api/creatives/${postId}/marquee-edit`,
      {
        method: 'POST',
        headers: {
          ...(await getAuthHeaders()),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rect, instruction }),
      },
    );
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return rowToPost(row);
}

/**
 * Marquee edit — PREVIEW mode (Jul 17). Renders the region edit + charges the
 * 1 credit but does NOT persist to the post. Returns a display URL; the caller
 * shows it and lets the user Keep (fork a new version via saveCreativeVersion)
 * or Discard (revert to the original). No refund on Discard — the render ran.
 */
export async function marqueeEditPreview(
  postId: string,
  rect: { x: number; y: number; w: number; h: number },
  instruction: string,
  /** Optional — edit THIS image (base64 PNG, data-URL prefix ok) instead of
   *  the post's server-side base. Used to region-edit a specific carousel frame
   *  (or the live, possibly-AI-edited frame). frameIndex is a breadcrumb. */
  opts?: { imageB64?: string; frameIndex?: number },
): Promise<{ previewUrl: string }> {
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/api/creatives/${postId}/marquee-edit`,
      {
        method: 'POST',
        headers: {
          ...(await getAuthHeaders()),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rect, instruction, preview: true,
          ...(opts?.imageB64 ? { image_b64: opts.imageB64 } : {}),
          ...(opts?.frameIndex != null ? { frame_index: opts.frameIndex } : {}),
        }),
      },
    );
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const data: { preview_url: string; preview_path: string } = await response.json();
  return { previewUrl: data.preview_url };
}

/**
 * Marquee undo — pops the latest entry off base_image_path_history.
 * Free (no credit). Returns the updated Post.
 */
export async function marqueeUndoPost(postId: string): Promise<Post> {
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/api/creatives/${postId}/marquee-undo`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
      },
    );
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return rowToPost(row);
}

export async function reimaginePost(
  postId: string,
  prompt: string,
): Promise<Post> {
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/api/gallery/posts/${postId}/reimagine`,
      {
        method: 'POST',
        headers: {
          ...(await getAuthHeaders()),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      },
    );
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    );
  }
  if (!response.ok) throw await mapFailure(response);
  const row: RawGalleryRow = await response.json();
  return rowToPost(row);
}
